import type {
  ContactSource,
  DebtPaymentEvidence,
  DebtPaymentMethod,
  DebtStatus,
  DebtStatusSource,
} from '@/shared/types/contacts';
import { normalizePhone } from '@/util/validation/patterns';

export const MIN_CONTACT_SEARCH_LENGTH = 3;

const SEARCHABLE_TRIGRAM_PATTERN = /[\p{L}\p{N}]{3}/u;
export const CONTACT_PHONE_SHAPE = /^[+(]?[\d\s().-]+$/;

const isSearchable = (term: string) =>
  term.length >= MIN_CONTACT_SEARCH_LENGTH && SEARCHABLE_TRIGRAM_PATTERN.test(term);

export const contactSearchTerm = (value?: string): string | undefined => {
  const term = value?.normalize('NFKC').trim();
  if (!term) {
    return undefined;
  }
  if (isSearchable(term)) {
    return term;
  }
  const phone = CONTACT_PHONE_SHAPE.test(term) ? normalizePhone(term) : '';
  return isSearchable(phone) ? phone : undefined;
};

export const sourceLabelKey: Record<ContactSource, string> = {
  inbound: 'contacts.source_inbound',
  outbound: 'contacts.source_outbound',
  manual: 'contacts.source_manual',
};

export const sourceFlavour: Record<ContactSource, 'informational' | 'ai' | 'neutral'> = {
  inbound: 'informational',
  outbound: 'ai',
  manual: 'neutral',
};

const AVATAR_COLORS = ['bg-avatar-1', 'bg-avatar-2', 'bg-avatar-3', 'bg-avatar-4', 'bg-avatar-5'];

// Pick a stable avatar color per contact by hashing its id, so the same contact shows
// the same color everywhere (list table and detail sidebar). Deterministic — not random
// at render — is what keeps the two views consistent.
export const getContactAvatarColor = (contactId?: string): string => {
  if (!contactId) {
    return AVATAR_COLORS[0];
  }
  let hash = 0;
  for (let i = 0; i < contactId.length; i++) {
    hash = (hash * 31 + contactId.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const getInitials = (name?: string) => {
  if (!name) {
    return '?';
  }
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const DEBT_STATUS_KEY: Record<DebtStatus, string> = {
  active: 'contacts.debts.status_value.active',
  paid: 'contacts.debts.status_value.paid',
  settled: 'contacts.debts.status_value.settled',
  disputed: 'contacts.debts.status_value.disputed',
  legal: 'contacts.debts.status_value.legal',
  charged_off: 'contacts.debts.status_value.charged_off',
  bankruptcy: 'contacts.debts.status_value.bankruptcy',
};

export const DEBT_PAYMENT_METHODS: DebtPaymentMethod[] = [
  'pix',
  'boleto',
  'card',
  'bank_transfer',
  'cash',
  'check',
  'other',
];

export const PAYMENT_METHOD_KEY: Record<DebtPaymentMethod, string> = {
  pix: 'contacts.debts.payment_method_value.pix',
  boleto: 'contacts.debts.payment_method_value.boleto',
  card: 'contacts.debts.payment_method_value.card',
  bank_transfer: 'contacts.debts.payment_method_value.bank_transfer',
  cash: 'contacts.debts.payment_method_value.cash',
  check: 'contacts.debts.payment_method_value.check',
  other: 'contacts.debts.payment_method_value.other',
};

export const PAYMENT_EVIDENCE_KEY: Record<DebtPaymentEvidence, string> = {
  verified: 'contacts.debts.payment_evidence_value.verified',
  reported: 'contacts.debts.payment_evidence_value.reported',
};

export const DEBT_STATUS_SOURCE_KEY: Record<DebtStatusSource, string> = {
  api: 'contacts.debts.history_source.api',
  nba: 'contacts.debts.history_source.nba',
  subscriber_upload: 'contacts.debts.history_source.subscriber_upload',
};
